import L from 'leaflet';
import proj4 from 'proj4';
import { distancePointToSegment, normalizeAngleDegrees } from 'utils/geometryHelper.js';
import type { Point2D } from 'utils/geometryHelper.js';

const LV95_PROJ =
  '+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 +x_0=2600000 +y_0=1200000 +ellps=bessel +towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs';
const AXIS_SNAP_DEGREES = 5;
const AXIS_SNAP_TAN = Math.tan((AXIS_SNAP_DEGREES * Math.PI) / 180);

if (!proj4.defs('EPSG:2056')) {
  proj4.defs('EPSG:2056', LV95_PROJ);
}

const toMapPoint = (latlng: L.LatLng, map: L.Map | null | undefined): L.Point | Point2D => {
  if (map?.latLngToLayerPoint) {
    return map.latLngToLayerPoint(latlng);
  }
  return { x: latlng.lng, y: latlng.lat };
};

const toLv95Point = (latlng: L.LatLng): Point2D => {
  const [x, y] = proj4('EPSG:4326', 'EPSG:2056', [latlng.lng, latlng.lat]);
  return { x, y };
};

// Canonical endpoint ordering to make side angle independent from polygon vertex order.
const orderSegmentEndpoints = (pointA: Point2D, pointB: Point2D): [Point2D, Point2D] => {
  const dx = pointB.x - pointA.x;
  const dy = pointB.y - pointA.y;
  const isVertical = Math.abs(dy) > 0 && Math.abs(dx / dy) <= AXIS_SNAP_TAN;

  if (isVertical) {
    if (pointA.y < pointB.y) {
      return [pointA, pointB];
    }
    if (pointA.y > pointB.y) {
      return [pointB, pointA];
    }
  }

  if (pointA.x < pointB.x) {
    return [pointA, pointB];
  }
  if (pointA.x > pointB.x) {
    return [pointB, pointA];
  }
  if (pointA.y <= pointB.y) {
    return [pointA, pointB];
  }
  return [pointB, pointA];
};

const getSelectedSideAngle = (
  latlng: L.LatLng,
  latlngs: L.LatLng[] | null | undefined,
  mapInstance: L.Map | null | undefined,
  maxDistancePx: number
): number | null => {
  if (!latlngs || latlngs.length < 2) {
    return null;
  }
  const clickPoint = toMapPoint(latlng, mapInstance);
  let closestDistance = Infinity;
  let closestAngle = null;
  for (let i = 0; i < latlngs.length; i += 1) {
    const nextIndex = (i + 1) % latlngs.length;
    const startPoint = toMapPoint(latlngs[i], mapInstance);
    const endPoint = toMapPoint(latlngs[nextIndex], mapInstance);
    const distance = distancePointToSegment(clickPoint, startPoint, endPoint);
    if (distance < closestDistance) {
      closestDistance = distance;
      const startLv95 = toLv95Point(latlngs[i]);
      const endLv95 = toLv95Point(latlngs[nextIndex]);
      const [segmentStart, segmentEnd] = orderSegmentEndpoints(startLv95, endLv95);
      const angleRadians = Math.atan2(segmentEnd.y - segmentStart.y, segmentEnd.x - segmentStart.x);
      closestAngle = normalizeAngleDegrees((angleRadians * 180) / Math.PI);
    }
  }
  if (closestDistance > maxDistancePx) {
    return null;
  }
  return closestAngle;
};

const getClosestPolygonSegmentIndex = (
  latlng: L.LatLng,
  latlngs: L.LatLng[] | null | undefined,
  mapInstance: L.Map | null | undefined,
  maxDistancePx: number
): number | null => {
  if (!latlngs || latlngs.length < 2) {
    return null;
  }

  const clickPoint = toMapPoint(latlng, mapInstance);
  let closestDistance = Infinity;
  let closestIndex = null;

  for (let index = 0; index < latlngs.length; index += 1) {
    const nextIndex = (index + 1) % latlngs.length;
    const startPoint = toMapPoint(latlngs[index], mapInstance);
    const endPoint = toMapPoint(latlngs[nextIndex], mapInstance);
    const distance = distancePointToSegment(clickPoint, startPoint, endPoint);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  }

  if (closestDistance > maxDistancePx) {
    return null;
  }

  return closestIndex;
};

const normalizeRectangle = (layer: L.Polygon): L.Polygon => {
  const map = (layer as L.Polygon & { _map?: L.Map })?._map;
  if (!map || !layer?.getLatLngs) {
    return layer;
  }
  const latLngs = layer.getLatLngs() as L.LatLng[] | L.LatLng[][];
  const ring = (Array.isArray(latLngs?.[0]) ? latLngs[0] : latLngs) as L.LatLng[];
  if (!ring || ring.length < 4) {
    return layer;
  }
  const zoom = map.getZoom();
  const points = ring.map((latLng) => map.project(latLng, zoom));
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const corners = [
    map.unproject([minX, minY], zoom),
    map.unproject([maxX, minY], zoom),
    map.unproject([maxX, maxY], zoom),
    map.unproject([minX, maxY], zoom),
  ];
  const polygonLayer = L.polygon(corners, layer.options);
  polygonLayer.addTo(map);
  layer.remove();
  return polygonLayer;
};

const getAlignedRectangle = (
  startLatLng: L.LatLng | null | undefined,
  endLatLng: L.LatLng | null | undefined,
  map: L.Map | null | undefined
): L.LatLng[] => {
  if (!startLatLng || !endLatLng || !map?.latLngToLayerPoint || !map?.layerPointToLatLng) {
    return [];
  }
  const startPoint = map.latLngToLayerPoint(startLatLng);
  const endPoint = map.latLngToLayerPoint(endLatLng);
  const minX = Math.min(startPoint.x, endPoint.x);
  const maxX = Math.max(startPoint.x, endPoint.x);
  const minY = Math.min(startPoint.y, endPoint.y);
  const maxY = Math.max(startPoint.y, endPoint.y);
  return [
    map.layerPointToLatLng(L.point(minX, minY)),
    map.layerPointToLatLng(L.point(maxX, minY)),
    map.layerPointToLatLng(L.point(maxX, maxY)),
    map.layerPointToLatLng(L.point(minX, maxY)),
  ];
};

const hasMinimumPolygonVertices = (ring: unknown): ring is L.LatLng[] => Array.isArray(ring) && ring.length >= 3;

type DrawPolylineHandler = L.Handler & {
  type?: string;
  _map: L.Map;
  _markers: Array<{ getLatLng?: () => L.LatLng }>;
  _mouseMarker: { setLatLng: (latlng: L.LatLng) => void };
  _currentLatLng: L.LatLng;
  _mouseDownOrigin: L.Point | null;
  options: { maxPoints?: number };
  _onMouseMove?: (event: L.LeafletMouseEvent & { originalEvent?: MouseEvent | TouchEvent | PointerEvent }) => void;
  _endPoint?: (
    clientX: number,
    clientY: number,
    event: L.LeafletMouseEvent & { originalEvent?: MouseEvent | TouchEvent | PointerEvent; latlng: L.LatLng }
  ) => void;
  _updateTooltip: (latlng?: L.LatLng) => void;
  _updateGuide: (point?: L.Point) => void;
  _calculateFinishDistance: (latlng: L.LatLng) => number;
  addVertex: (latlng: L.LatLng) => void;
  _finishShape: () => void;
  _enableNewMarkers: () => void;
};

const isShiftConstrainedPolygonEvent = (
  handler: DrawPolylineHandler,
  event: { originalEvent?: MouseEvent | TouchEvent | PointerEvent } | null | undefined
): boolean => {
  if (handler.type !== 'polygon' || (handler._markers?.length ?? 0) < 2) {
    return false;
  }
  const originalEvent = event?.originalEvent;
  return Boolean(originalEvent && 'shiftKey' in originalEvent && originalEvent.shiftKey);
};

const getRightAngleSnappedLatLng = (handler: DrawPolylineHandler, latlng: L.LatLng): L.LatLng => {
  const markerCount = handler._markers?.length ?? 0;
  if (markerCount < 2) {
    return latlng;
  }

  const previousLatLng = handler._markers[markerCount - 2]?.getLatLng?.();
  const lastLatLng = handler._markers[markerCount - 1]?.getLatLng?.();
  if (!previousLatLng || !lastLatLng) {
    return latlng;
  }

  const previousPoint = toLv95Point(previousLatLng);
  const lastPoint = toLv95Point(lastLatLng);
  const currentPoint = toLv95Point(latlng);
  const segmentX = lastPoint.x - previousPoint.x;
  const segmentY = lastPoint.y - previousPoint.y;
  const segmentLength = Math.hypot(segmentX, segmentY);

  if (!segmentLength) {
    return latlng;
  }

  const perpendicularUnitX = -segmentY / segmentLength;
  const perpendicularUnitY = segmentX / segmentLength;
  const deltaX = currentPoint.x - lastPoint.x;
  const deltaY = currentPoint.y - lastPoint.y;
  const projectedDistance = deltaX * perpendicularUnitX + deltaY * perpendicularUnitY;
  const snappedPointX = lastPoint.x + projectedDistance * perpendicularUnitX;
  const snappedPointY = lastPoint.y + projectedDistance * perpendicularUnitY;
  const [lng, lat] = proj4('EPSG:2056', 'EPSG:4326', [snappedPointX, snappedPointY]);

  return L.latLng(lat, lng);
};

const alignRectangleDrawing = () => {
  if (!L.Draw?.Rectangle || L.Draw.Rectangle.prototype._umScreenAlignedPreviewPatched) {
    return;
  }
  L.Draw.Rectangle.prototype._drawShape = function (this: L.Draw.Rectangle, latlng: L.LatLng) {
    const corners = getAlignedRectangle(this._startLatLng, latlng, this._map);
    if (!corners.length) {
      return;
    }
    if (this._shape) {
      this._shape.setLatLngs(corners);
      return;
    }
    this._shape = new L.Polygon(corners, this.options.shapeOptions);
    this._map.addLayer(this._shape);
  };
  L.Draw.Rectangle.prototype._fireCreatedEvent = function (this: L.Draw.Rectangle) {
    const latLngs = this._shape?.getLatLngs?.() as L.LatLng[] | L.LatLng[][] | undefined;
    const ring = Array.isArray(latLngs?.[0]) ? latLngs[0] : latLngs;
    if (!hasMinimumPolygonVertices(ring)) {
      return;
    }
    const rectangleLayer = new L.Polygon(ring, this.options.shapeOptions);
    L.Draw.Feature.prototype._fireCreatedEvent.call(this, rectangleLayer);
  };
  L.Draw.Rectangle.prototype._umScreenAlignedPreviewPatched = true;
};

const enablePolygonRightAngleSnapDrawing = () => {
  const drawPolylinePrototype = (L.Draw as typeof L.Draw & { Polyline?: { prototype: Record<string, unknown> } })
    .Polyline?.prototype as (DrawPolylineHandler & { _umRightAngleSnapPatched?: boolean }) | undefined;
  if (!drawPolylinePrototype || drawPolylinePrototype._umRightAngleSnapPatched) {
    return;
  }

  drawPolylinePrototype._onMouseMove = function (
    this: DrawPolylineHandler,
    event: L.LeafletMouseEvent & { originalEvent?: MouseEvent | TouchEvent | PointerEvent }
  ) {
    const layerPoint = this._map.mouseEventToLayerPoint(event.originalEvent);
    let latlng = this._map.layerPointToLatLng(layerPoint);
    if (isShiftConstrainedPolygonEvent(this, event)) {
      latlng = getRightAngleSnappedLatLng(this, latlng);
    }
    this._currentLatLng = latlng;
    this._updateTooltip(latlng);
    this._updateGuide(this._map.latLngToLayerPoint(latlng));
    this._mouseMarker.setLatLng(latlng);
    L.DomEvent.preventDefault(event.originalEvent);
  };

  drawPolylinePrototype._endPoint = function (
    this: DrawPolylineHandler,
    clientX: number,
    clientY: number,
    event: L.LeafletMouseEvent & { originalEvent?: MouseEvent | TouchEvent | PointerEvent; latlng: L.LatLng }
  ) {
    if (!this._mouseDownOrigin) {
      this._mouseDownOrigin = null;
      return;
    }

    const dragDistance = L.point(clientX, clientY).distanceTo(this._mouseDownOrigin);
    const constrainedLatLng = isShiftConstrainedPolygonEvent(this, event)
      ? getRightAngleSnappedLatLng(this, event.latlng)
      : event.latlng;
    const finishDistance = this._calculateFinishDistance(constrainedLatLng);

    if ((this.options.maxPoints ?? 0) > 1 && this.options.maxPoints === this._markers.length + 1) {
      this.addVertex(constrainedLatLng);
      this._finishShape();
    } else if (finishDistance < 10 && L.Browser.touch) {
      this._finishShape();
    } else if (Math.abs(dragDistance) < 9 * (window.devicePixelRatio || 1)) {
      this.addVertex(constrainedLatLng);
    }

    this._enableNewMarkers();
    this._mouseDownOrigin = null;
  };

  drawPolylinePrototype._umRightAngleSnapPatched = true;
};

type ShapeType = 'polygon' | 'rectangle' | null;
type BorderType = 'legalBorder' | 'buildableBorder' | 'analysisArea' | null;

const triggerDrawing = (shapeType: ShapeType, _borderType: BorderType): void => {
  const drawControlClass = shapeType === 'polygon' ? 'leaflet-draw-draw-polygon' : 'leaflet-draw-draw-rectangle';
  const drawControl = document.getElementsByClassName(drawControlClass)[0] as HTMLElement | undefined;
  if (drawControl) {
    drawControl.click();
  }
};

const clearActiveDrawButton = (): void => {
  const previous = document.querySelector<HTMLButtonElement>('button[data-draw-active="true"]');
  if (previous) {
    previous.removeAttribute('data-draw-active');
    previous.classList.remove('draw-button-active');
    previous.style.removeProperty('background-color');
    previous.style.removeProperty('box-shadow');
    previous.style.removeProperty('filter');
  }
};

const isDrawButtonActive = (shapeType: ShapeType, borderType: BorderType): boolean => {
  const selector = `button[data-draw-border-type="${borderType}"][data-draw-shape="${shapeType}"]`;
  return document.querySelector(selector)?.getAttribute('data-draw-active') === 'true';
};

const highlightActiveDrawButton = (shapeType: ShapeType, borderType: BorderType): void => {
  clearActiveDrawButton();
  const selector = `button[data-draw-border-type="${borderType}"][data-draw-shape="${shapeType}"]`;
  const button = document.querySelector<HTMLButtonElement>(selector);
  if (!button) {
    return;
  }
  const computedBackground = window.getComputedStyle(button).backgroundColor;
  if (computedBackground) {
    button.style.backgroundColor = computedBackground;
  }
  button.setAttribute('data-draw-active', 'true');
  button.classList.add('draw-button-active');
};

interface CancelActiveDrawingRefs {
  isDrawingRef?: { current: boolean } | null;
  pendingDrawRef?: { current: unknown } | null;
  currentTypeOfPolygonRef?: { current: BorderType } | null;
  currentShapeTypeRef?: { current: ShapeType } | null;
}

const cancelActiveDrawing = ({
  isDrawingRef,
  pendingDrawRef,
  currentTypeOfPolygonRef,
  currentShapeTypeRef,
}: CancelActiveDrawingRefs): void => {
  const actionLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('.leaflet-draw-actions a'));
  const cancelLink = actionLinks.find((link) => {
    const title = (link.getAttribute('title') || '').toLowerCase();
    return title.includes('cancel') || title.includes('annulla');
  });
  const fallbackLink = actionLinks[actionLinks.length - 1];
  if (cancelLink || fallbackLink) {
    (cancelLink || fallbackLink).click();
  } else {
    const escEvent = new KeyboardEvent('keydown', {
      key: 'Escape',
      code: 'Escape',
      keyCode: 27,
      which: 27,
      bubbles: true,
    });
    document.dispatchEvent(escEvent);
  }
  if (isDrawingRef) {
    isDrawingRef.current = false;
  }
  if (pendingDrawRef) {
    pendingDrawRef.current = null;
  }
  if (currentTypeOfPolygonRef) {
    currentTypeOfPolygonRef.current = null;
  }
  if (currentShapeTypeRef) {
    currentShapeTypeRef.current = null;
  }
  clearActiveDrawButton();
};

export {
  toMapPoint,
  getSelectedSideAngle,
  getClosestPolygonSegmentIndex,
  normalizeRectangle,
  alignRectangleDrawing,
  enablePolygonRightAngleSnapDrawing,
  triggerDrawing,
  clearActiveDrawButton,
  isDrawButtonActive,
  highlightActiveDrawButton,
  cancelActiveDrawing,
};
